import { createLazyFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { Section } from "@/features/admin/components/Section";
import { FieldSetDetailForm } from "@/features/admin/components/tubes/FieldSetDetailForm";
import { useTabSearch } from "@/features/admin/hooks";
import { mutationResponse } from "@/utils/trpc";

export const Route = createLazyFileRoute(
  "/_auth/admin/tubes/field-sets/$id/edit",
)({
  component: () => {
    const params = Route.useParams();
    return <EditFieldSetPage id={params.id} />;
  },
});

function EditFieldSetPage(params: { id: number }) {
  const tabsProps = useTabSearch();
  const { data, isPending, error } =
    trpc.admin.sampleFieldSet.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  const fieldSet = data;

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/tubes" }}>Tubes</Breadcrumb>
            <Breadcrumb
              href={{ to: "/admin/tubes", search: { t: "field-sets" } }}
            >
              Field sets
            </Breadcrumb>
            <Breadcrumb>{fieldSet.name}</Breadcrumb>
          </>
        }
        actions={
          <DeleteFieldSetDialog fieldSet={fieldSet}>
            <Button variant="destructive">Delete</Button>
          </DeleteFieldSetDialog>
        }
      >
        {fieldSet.name}
      </Section.Title>
      <Tabs {...tabsProps} className="flex-1">
        <TabsList>
          <Tab id="details">Details</Tab>
        </TabsList>
        <TabPanel id="details">
          <FieldSetDetailForm fieldSet={fieldSet} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeleteFieldSetDialog({
  children,
  fieldSet,
}: {
  children: ReactNode;
  fieldSet: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deleteFieldSet = trpc.admin.sampleFieldSet.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deleteFieldSet.mutate(
      fieldSet,
      mutationResponse({
        errorTitle: "Error deleting tube",
        successMessage: "Tube deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/tubes" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete field set"
      message="Are you sure you want to delete this field set?"
      onConfirm={onConfirm}
      loading={deleteFieldSet.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
